// The hand-maintained runtime of this package. See packages/api-contract/README.md.
//
// Everything under src/generated is produced from packages/api-contract/openapi.yaml
// and CI fails if it is stale. This file is the thin, stable runtime around it: a
// typed fetch client, bearer-token attachment, and two narrowing helpers for the
// masked/revealed referral card. It holds no endpoint knowledge — adding an endpoint
// means editing the contract, not this file.

import createFetchClient, { type Client, type Middleware } from "openapi-fetch";

import type { components, paths } from "./generated/schema";

/** A fully typed Match Point API client. Every path, param and body comes from the contract. */
export type MatchPointClient = Client<paths>;

export interface MatchPointClientOptions {
  /** API Gateway base URL for the environment. No trailing slash. */
  baseUrl: string;
  /**
   * Returns the current Cognito ID token, or `undefined` when signed out. Called
   * per request rather than captured once, so a token refresh does not require a
   * new client. Unauthenticated routes ignore it.
   */
  getToken?: () => string | undefined | Promise<string | undefined>;
  /** Override `fetch`, for tests or for a retrying wrapper. */
  fetch?: ClientFetch;
}

type ClientFetch = (input: Request) => Promise<Response>;

/**
 * Builds a client for one environment.
 *
 * Note what is deliberately *not* here: nothing generates an `Idempotency-Key` for
 * you. The contract marks the header required on every mutating operation, so the
 * generated types make omitting it a compile error — a stronger guarantee than a
 * client-side default, which would let a caller forget and be silently covered.
 * Use `newIdempotencyKey()` once per user-initiated submit and reuse it only if you
 * retry that same submit.
 */
export function createMatchPointClient(options: MatchPointClientOptions): MatchPointClient {
  const client = createFetchClient<paths>({
    baseUrl: options.baseUrl,
    headers: { Accept: "application/json, application/problem+json" },
    ...(options.fetch ? { fetch: options.fetch } : {}),
  });

  const getToken = options.getToken;
  if (getToken) {
    const auth: Middleware = {
      async onRequest({ request }) {
        const token = await getToken();
        if (token) request.headers.set("Authorization", `Bearer ${token}`);
        return request;
      },
    };
    client.use(auth);
  }

  return client;
}

/**
 * A fresh idempotency key. One per user-initiated submit: a transport retry of that
 * submit reuses the key and replays the original response, while a new submit — the
 * user correcting a typo and pressing the button again — needs a new one.
 */
export function newIdempotencyKey(): string {
  return globalThis.crypto.randomUUID();
}

/*
 * The three referral-card types and the two narrowing helpers stood here.
 *
 * `isRevealed` and `isMasked` narrowed a card to the variant that carries the
 * client's contact details, and the point of them was that a caller reached for
 * `disclosure` rather than `state === "accepted"` — `disclosure` described what a
 * response contained, `state` described the referral's lifecycle, and they
 * answered different questions. The masked type had no `contact` property at all,
 * so the compiler rather than a code review stopped a pre-accept read.
 *
 * ADR 0009 removed the referral plane, so the schemas they named are no longer in
 * the contract and these no longer compile. The pattern is recorded here because
 * this file is where a client author looks: if anything reintroduces a response
 * whose shape depends on a server-side disclosure decision, model it as two types
 * and a narrowing predicate rather than one type with optional fields and a
 * boolean.
 */

/** An RFC 9457 problem document. Switch on `type`, never on `title`. */
export type Problem = components["schemas"]["Problem"];

/**
 * Stable problem `type` URIs. The taxonomy is closed and mirrors `ProblemTypes` in
 * `services/platform/Platform/Errors/ProblemDocument.cs` — the server constants are
 * the source of truth, and `scripts/check-problem-types.mjs` asserts every value here
 * appears verbatim in the contract.
 *
 * Two errors carry no problem document at all, because API Gateway answers before the
 * service runs: a missing/invalid token is a 401 with a bare `{"message":"Unauthorized"}`
 * body, and throttling is a 429 with `{"message":"Too Many Requests"}`. Branch on the
 * status code for those, not on `type`.
 */
export const problemTypes = {
  validation: "https://matchpoint-health.com/problems/validation",
  malformedRequest: "https://matchpoint-health.com/problems/malformed-request",
  unauthenticated: "https://matchpoint-health.com/problems/unauthenticated",
  forbidden: "https://matchpoint-health.com/problems/forbidden",
  notFound: "https://matchpoint-health.com/problems/not-found",
  conflict: "https://matchpoint-health.com/problems/conflict",
  idempotencyKeyReused: "https://matchpoint-health.com/problems/idempotency-key-reused",
  timeout: "https://matchpoint-health.com/problems/timeout",
  unavailable: "https://matchpoint-health.com/problems/unavailable",
  internal: "https://matchpoint-health.com/problems/internal",
} as const;
